import React, { useState, useEffect, useRef, ChangeEvent, KeyboardEvent } from 'react';
import apiService from '../../services/ApiService';
import ToggleSwitch from '../common/Switch';
import { apiRoutes } from '../../shared/apiRoutes';
import { Party } from '../../shared/interfaces';
import { formatDateOnly, toISTDateString } from '../../utils/util';
import { ArrowsRightLeftIcon } from '@heroicons/react/20/solid';

interface HavalaRecord {
  id: number;
  partyCodeAndName: string;
  havalaCodeAndName: string;
  amount: number;
  remarks: string | null;
  recordDate: string;
  isDeleted?: boolean;
  createdAt?: string | Date; // Creation date from API
  modifiedAt?: string | Date; // Modified date from API
  lastModifiedBy: string;
  lastModifiedDate: string;
}

interface HavalaListResponse {
  items: HavalaRecord[];
  totalCount: number;
  startDate: string;
  endDate: string;
  partyId: number | null;
}

const Havala: React.FC = () => {
  // Date range state - default to previous Monday to Saturday
  const getPreviousMondayToSaturday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    let previousMonday: Date;
    
    if (dayOfWeek === 1) {
      // If today is Monday, use the previous Monday (7 days ago)
      previousMonday = new Date(today);
      previousMonday.setDate(today.getDate() - 7);
    } else {
      // Find the most recent Monday
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      previousMonday = new Date(today);
      previousMonday.setDate(today.getDate() - daysToSubtract);
    }
    
    // Saturday is 6 days after Monday
    const saturday = new Date(previousMonday);
    saturday.setDate(previousMonday.getDate() + 6);
    
    return {
      startDate: toISTDateString(previousMonday),
      endDate: toISTDateString(saturday)
    };
  };

  const dateRange = getPreviousMondayToSaturday();
  const [startDate, setStartDate] = useState(dateRange.startDate);
  const [endDate, setEndDate] = useState(dateRange.endDate);
  
  // Party filter state
  const [partyCode, setPartyCode] = useState<string>('');
  const [validatedPartyCode, setValidatedPartyCode] = useState<string>('');
  const [partyValidation, setPartyValidation] = useState<{ 
    isValid: boolean; 
    error: string; 
    partyName: string; 
    partyId: number | null 
  }>({
    isValid: false,
    error: '',
    partyName: '',
    partyId: null
  });
  const [parties, setParties] = useState<Party[]>([]);
  const partyInputRef = useRef<HTMLInputElement>(null);

  // Data state
  const [havalaRecords, setHavalaRecords] = useState<HavalaRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [showDeleted, setShowDeleted] = useState(false);

  // Fetch parties for validation
  useEffect(() => {
    apiService.post<{ data: Party[] }>(apiRoutes.parties.lite, {
      page: 1,
      pageSize: 1000,
      orderBy: 'partyCode',
      orderDirection: 'asc'
    })
      .then(response => {
        const partiesData = response.data || [];
        setParties(partiesData);
      })
      .catch(error => {
        console.error('Error fetching parties:', error);
      });
  }, []);

  // Focus on start date input when page loads
  useEffect(() => {
    const timer = setTimeout(() => {
      const startDateInput = document.querySelector('input[name="startDate"]') as HTMLInputElement;
      if (startDateInput) {
        startDateInput.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Validate party code format
  const validatePartyCodeFormat = (code: string): { isValid: boolean; error: string } => {
    if (!code || code.trim().length === 0) {
      return { isValid: false, error: 'Party code is required' };
    }
    if (code.length < 1) {
      return { isValid: false, error: 'Party code must be at least 1 character' };
    }
    if (code.length > 5) {
      return { isValid: false, error: 'Party code must be at most 5 characters' };
    }
    if (!/^[a-zA-Z0-9]+$/.test(code)) {
      return { isValid: false, error: 'Party code must be alphanumeric' };
    }
    return { isValid: true, error: '' };
  };

  // Validate and fetch party by code
  const validateAndFetchParty = async (code: string): Promise<{ isValid: boolean; error: string; partyName: string; partyId: number | null }> => {
    const codeMatch = code.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const trimmedCode = codeMatch ? codeMatch[1].toUpperCase().trim() : code.trim().toUpperCase();
    
    const formatValidation = validatePartyCodeFormat(trimmedCode);
    if (!formatValidation.isValid) {
      const validationResult = {
        isValid: false,
        error: formatValidation.error,
        partyName: '',
        partyId: null
      };
      setPartyValidation(validationResult);
      setValidatedPartyCode('');
      return validationResult;
    }

    // Check if party exists in local parties list first
    const filteredParties = (parties || []).filter(p => p.id !== 1);
    const foundParty = filteredParties.find(p => p.partyCode.toUpperCase() === trimmedCode);
    
    if (foundParty) {
      const validationResult = {
        isValid: true,
        error: '',
        partyName: foundParty.partyName,
        partyId: foundParty.id
      };
      setPartyValidation(validationResult);
      setValidatedPartyCode(trimmedCode);
      if (partyInputRef.current) {
        partyInputRef.current.value = `${foundParty.partyCode} - ${foundParty.partyName}`;
      }
      setPartyCode(trimmedCode);
      return validationResult;
    }

    // If not found locally, try to fetch from API
    try {
      const partyData = await apiService.get(apiRoutes.parties.getByCode(trimmedCode));
      if (partyData && partyData.id && partyData.id !== 1) {
        const validationResult = {
          isValid: true,
          error: '',
          partyName: partyData.partyName || '',
          partyId: partyData.id
        };
        setPartyValidation(validationResult);
        setValidatedPartyCode(trimmedCode);
        if (partyInputRef.current) {
          partyInputRef.current.value = `${partyData.partyCode} - ${partyData.partyName}`;
        }
        setPartyCode(trimmedCode);
        return validationResult;
      } else {
        const validationResult = {
          isValid: false,
          error: 'Invalid party code',
          partyName: '',
          partyId: null
        };
        setPartyValidation(validationResult);
        setValidatedPartyCode('');
        return validationResult;
      }
    } catch (error) {
      const validationResult = {
        isValid: false,
        error: 'Invalid party code',
        partyName: '',
        partyId: null
      };
      setPartyValidation(validationResult);
      setValidatedPartyCode('');
      return validationResult;
    }
  };

  // Handle party code input change
  const handlePartyCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeOnly = codeMatch ? codeMatch[1].toUpperCase() : value.toUpperCase();
    
    setPartyCode(codeOnly);
    
    // Clear validation if code changes
    if (codeOnly !== validatedPartyCode) {
      setPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      setValidatedPartyCode('');
    }
    
    // Update input to show only code
    if (partyInputRef.current && value !== codeOnly) {
      partyInputRef.current.value = codeOnly;
    }
  };

  // Handle party code blur
  const handlePartyCodeBlur = async () => {
    const inputRef = partyInputRef.current;
    const value = inputRef?.value || partyCode || '';
    
    if (!value || value.trim().length === 0) {
      // Clear party filter if empty
      setPartyCode('');
      setValidatedPartyCode('');
      setPartyValidation({
        isValid: false,
        error: '',
        partyName: '',
        partyId: null
      });
      if (inputRef) {
        inputRef.value = '';
      }
      return;
    }
    
    const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
    const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
    
    if (codeToValidate && codeToValidate !== validatedPartyCode) {
      await validateAndFetchParty(codeToValidate);
    }
  };

  // Handle party code Enter key
  const handlePartyCodeEnter = async (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      
      const inputRef = partyInputRef.current;
      const value = inputRef?.value || partyCode || '';
      
      if (!value || value.trim().length === 0) {
        // If empty, focus on start date
        const startDateInput = document.querySelector('input[name="startDate"]') as HTMLInputElement;
        if (startDateInput) {
          startDateInput.focus();
        }
        return;
      }
      
      const codeMatch = value.match(/^([A-Z0-9]+)(\s*-\s*.*)?$/i);
      const codeToValidate = codeMatch ? codeMatch[1].toUpperCase().trim() : value.trim().toUpperCase();
      
      if (codeToValidate && codeToValidate !== validatedPartyCode) {
        await validateAndFetchParty(codeToValidate);
      } else if (partyValidation.isValid) {
        // If already validated, move to start date
        const startDateInput = document.querySelector('input[name="startDate"]') as HTMLInputElement;
        if (startDateInput) {
          startDateInput.focus();
        }
      }
    }
  };

  // Fetch Havala records
  const fetchHavalaRecords = async () => {
    // Validate date range
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: 'Please select both start and end dates', type: 'error' } 
      }));
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setError('Start date cannot be later than end date');
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: 'Start date cannot be later than end date', type: 'error' } 
      }));
      return;
    }

    // Check date range (max 365 days)
    const start = new Date(startDate);
    const end = new Date(endDate);
    const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    if (daysDiff > 365) {
      setError('Date range cannot exceed 365 days');
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: 'Date range cannot exceed 365 days', type: 'error' } 
      }));
      return;
    }

    // If party code is entered, validate it first
    if (partyCode && partyCode.trim().length > 0 && !partyValidation.isValid) {
      setError('Please enter a valid party code');
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: 'Please enter a valid party code', type: 'error' } 
      }));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Format dates to ISO 8601 with timezone
      const startDateTime = new Date(startDate + 'T00:00:00Z').toISOString();
      const endDateTime = new Date(endDate + 'T23:59:59Z').toISOString();

      const requestBody: {
        startDate: string;
        endDate: string;
        partyId?: number;
        includeDeleted?: boolean;
      } = {
        startDate: startDateTime,
        endDate: endDateTime,
        includeDeleted: showDeleted
      };

      // Add partyId only if party is validated
      if (partyValidation.isValid && partyValidation.partyId) {
        requestBody.partyId = partyValidation.partyId;
      }

      const response = await apiService.post<HavalaListResponse>(
        apiRoutes.transactions.havalaListByDateRange,
        requestBody
      );

      setHavalaRecords(response.items || []);
      setTotalCount(response.totalCount || 0);
      
      if (response.items && response.items.length === 0) {
        window.dispatchEvent(new CustomEvent('show-toast', { 
          detail: { message: 'No Havala transactions found for the selected criteria', type: 'info' } 
        }));
      }
    } catch (err: any) {
      console.error('Error fetching Havala records:', err);
      const errorMessage = err?.response?.data?.error || err?.response?.data?.details || 'Failed to fetch Havala records';
      setError(errorMessage);
      setHavalaRecords([]);
      setTotalCount(0);
      window.dispatchEvent(new CustomEvent('show-toast', { 
        detail: { message: errorMessage, type: 'error' } 
      }));
    } finally {
      setLoading(false);
    }
  };

  // Handle date change
  const handleDateChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === 'startDate') {
      setStartDate(value);
      // Validate that end date is not before start date
      if (endDate && value > endDate) {
        window.dispatchEvent(new CustomEvent('show-toast', { 
          detail: { message: 'Start date cannot be later than end date', type: 'error' } 
        }));
      }
    } else if (name === 'endDate') {
      setEndDate(value);
      // Validate that end date is not before start date
      if (startDate && value < startDate) {
        window.dispatchEvent(new CustomEvent('show-toast', { 
          detail: { message: 'End date cannot be earlier than start date', type: 'error' } 
        }));
      }
    }
  };

  // Handle Enter key on date inputs
  const handleDateKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = e.target as HTMLInputElement;
      if (target.name === 'startDate') {
        const endDateInput = document.querySelector('input[name="endDate"]') as HTMLInputElement;
        if (endDateInput) {
          endDateInput.focus();
        }
      } else if (target.name === 'endDate') {
        fetchHavalaRecords();
      }
    }
  };

  // Format date for table columns (DD/MM/YYYY in IST, no time)
  const formatDisplayDate = (dateString: string): string => formatDateOnly(dateString) || dateString;

  const formatDisplayDateTime = (dateString: string): string => formatDateOnly(dateString) || dateString;

  // Format amount for display
  const formatAmount = (amount: number): string => {
    const displayAmount = amount ? (amount / 100) : 0;
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(displayAmount);
  };

  return (
    <div className="p-4 w-full mx-auto bg-gradient-to-br from-slate-50 to-slate-100 min-h-screen">
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-md mb-4 border border-slate-200">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center p-4 gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-2 rounded-lg shadow-sm">
              <ArrowsRightLeftIcon className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800">Havala Report</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                View Havala transactions by record date (business date), not created-on time
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-md mb-4 border border-slate-200">
        <div className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Party Code Filter */}
            <div className="flex items-center gap-2">
              <label htmlFor="partyCode" className="text-slate-700 text-sm whitespace-nowrap font-medium">
                Party
              </label>
              <div className="w-[150px]">
                <input
                  ref={partyInputRef}
                  id="partyCode"
                  name="partyCode"
                  type="text"
                  onChange={handlePartyCodeChange}
                  onBlur={handlePartyCodeBlur}
                  onKeyDown={handlePartyCodeEnter}
                  maxLength={50}
                  placeholder="Party Code"
                  disabled={loading}
                  className={`w-full h-[36px] px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white text-slate-900 ${
                    partyValidation.error ? 'border-red-500' : 
                    partyValidation.isValid ? 'border-green-500' : 
                    'border-slate-300'
                  }`}
                />
                {partyValidation.error && (
                  <div className="text-red-600 text-xs mt-1">{partyValidation.error}</div>
                )}
              </div>
            </div>

            {/* Start Date */}
            <div className="flex items-center gap-2">
              <label htmlFor="startDate" className="text-slate-700 text-sm whitespace-nowrap font-medium">
                Start Date
              </label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={startDate}
                onChange={handleDateChange}
                onKeyDown={handleDateKeyDown}
                disabled={loading}
                className="w-[150px] h-[36px] px-3 py-2 border border-slate-300 bg-white text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                required
              />
            </div>

            {/* End Date */}
            <div className="flex items-center gap-2">
              <label htmlFor="endDate" className="text-slate-700 text-sm whitespace-nowrap font-medium">
                End Date
              </label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={endDate}
                onChange={handleDateChange}
                onKeyDown={handleDateKeyDown}
                disabled={loading}
                className="w-[150px] h-[36px] px-3 py-2 border border-slate-300 bg-white text-slate-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                required
              />
            </div>

            {/* Show Deleted Toggle */}
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <ToggleSwitch
                  enabled={showDeleted}
                  onChange={setShowDeleted}
                  size="compact"
                />
                <span className="text-slate-700 text-sm whitespace-nowrap font-medium">Show deleted</span>
              </label>
            </div>

            {/* View Report Button */}
            <div className="flex items-center gap-2">
              <button
                onClick={fetchHavalaRecords}
                disabled={loading}
                className="h-[36px] px-4 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-blue-400 disabled:to-blue-400 text-white text-sm font-medium rounded-lg shadow-sm transition-all duration-200 disabled:cursor-not-allowed"
              >
                {loading ? 'Loading...' : 'View Report'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Section */}
      {error && !loading && (
        <div className="mb-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Summary */}
      {totalCount > 0 && (
        <div className="mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              Total Records: <span className="font-bold">{totalCount}</span>
              {partyValidation.isValid && partyValidation.partyName && (
                <span className="ml-4">
                  Filtered by: <span className="font-bold">{partyValidation.partyName}</span>
                </span>
              )}
            </p>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-2 text-slate-600 text-sm font-medium">Loading Havala records...</p>
          </div>
        ) : havalaRecords.length === 0 && !error ? (
          <div className="p-8 text-center">
            <p className="text-slate-600 text-sm font-medium">No Havala transactions found</p>
            <p className="text-slate-500 text-sm mt-1">Try adjusting your date range or party filter</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-gradient-to-r from-slate-50 to-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Sender (PCode & Name)</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Receiver (Havala Code & Name)</th>
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Remarks</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Record Date</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Last Modified By</th>
                  <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Last Modified Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {havalaRecords.map((record, index) => (
                  <tr 
                    key={record.id} 
                    className={`hover:bg-blue-50 transition-colors duration-150 ${
                      record.isDeleted ? 'bg-slate-200' : index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    }`}
                  >
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-slate-900">
                      {record.partyCodeAndName}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-slate-900">
                      {record.havalaCodeAndName}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700 text-right font-semibold">
                      {formatAmount(record.amount)}
                    </td>
                    <td className="px-4 py-2 text-sm text-slate-700 max-w-xs truncate" title={record.remarks || ''}>
                      {record.remarks || '-'}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700">
                      {formatDisplayDate(record.recordDate)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700">
                      {record.lastModifiedBy}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-700">
                      {(() => {
                        const formattedDate = formatDisplayDateTime(record.lastModifiedDate);
                        // If deleted, show (deleted) at end; else show (Modified) if modified
                        const createdAt = record.createdAt ? new Date(record.createdAt).getTime() : null;
                        const modifiedAt = record.modifiedAt ? new Date(record.modifiedAt).getTime() : null;
                        const isModified = createdAt !== null && modifiedAt !== null && createdAt !== modifiedAt;
                        
                        return (
                          <>
                            {formattedDate}
                            {record.isDeleted ? (
                              <span className="ml-1 text-red-600 font-medium">(deleted)</span>
                            ) : isModified ? (
                              <span className="text-amber-600 ml-1">(Modified)</span>
                            ) : null}
                          </>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Havala;
